import { Bell, CircleHelp, Menu } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { BranchSelector } from "./BranchSelector";
import { MerchantSwitcher } from "./MerchantSwitcher";
import { SyncStatusChip } from "./SyncStatusChip";
import { UserMenu } from "./UserMenu";

export interface TopBarProps {
  title: string;
  subtitle?: string;
  /** Whether the navigation drawer is showing. Drives `aria-expanded`. */
  navOpen?: boolean;
  /** Opens and closes the drawer. Absent means no hamburger — see below. */
  onToggleNav?: () => void;
}

/**
 * The header from both designs: page title and subtitle on the left; branch
 * selector, sync status, notifications and the user chip on the right.
 *
 * **What drops on a narrow screen, and why that order.** Seven controls do not
 * fit across a phone, so they are shed from the least to the most urgent:
 *
 * *   The **subtitle** goes first. It explains a screen somebody is already
 *     looking at.
 * *   The **merchant switcher** and **branch selector** move into the drawer's
 *     territory — they are navigation, they are used once a session, and both
 *     are wide.
 * *   The **notification bell** goes at the narrowest sizes. It is a nice-to-know.
 * *   The **sync chip** never goes. The brief is explicit that the online/offline
 *     indicator is always visible, and it is the one control a cashier checks
 *     mid-sale.
 * *   The **user menu** never goes: it is how somebody signs out of a shared till.
 */
export function TopBar({
  title,
  subtitle,
  navOpen = false,
  onToggleNav,
}: TopBarProps) {
  const { t } = useTranslation();

  return (
    <header className="flex h-topbar shrink-0 items-center gap-2 border-b border-line bg-surface px-3 sm:gap-3 sm:px-4 lg:gap-4 lg:px-6">
      {/* ── The drawer handle ───────────────────────────────────────────────── */}
      {onToggleNav && (
        <button
          type="button"
          onClick={onToggleNav}
          aria-label={t("nav.menu", "Menu")}
          aria-expanded={navOpen}
          aria-controls="primary-navigation"
          className="grid size-touch shrink-0 place-items-center rounded-control text-ink-600 hover:bg-canvas hover:text-ink-900 lg:hidden"
        >
          <Menu className="size-5" aria-hidden />
        </button>
      )}

      {/* ── Page identity ───────────────────────────────────────────────────── */}
      <div className="min-w-0 flex-1">
        <h1 className="truncate text-page-title text-ink-900">{title}</h1>
        {subtitle && (
          <p className="hidden truncate text-page-subtitle text-ink-500 md:block">
            {subtitle}
          </p>
        )}
      </div>

      <div className="hidden xl:contents">
        <MerchantSwitcher />
      </div>
      <div className="hidden lg:contents">
        <BranchSelector />
      </div>

      <SyncStatusChip />

      {/* ── Help: the picture guide, one tap from the till ──────────────────── */}
      <Link
        to="/help"
        aria-label="Help: picture guide"
        title="How do I…?"
        className="grid size-touch shrink-0 place-items-center rounded-control text-ink-500 hover:bg-canvas hover:text-ink-900"
      >
        <CircleHelp className="size-5" aria-hidden />
      </Link>

      {/* ── Notifications ───────────────────────────────────────────────────── */}
      <button
        type="button"
        aria-label={t("topbar.notifications")}
        className="relative hidden size-touch shrink-0 place-items-center rounded-control text-ink-500 hover:bg-canvas hover:text-ink-900 sm:grid"
      >
        <Bell className="size-5" aria-hidden />
      </button>

      <span className="hidden h-8 w-px shrink-0 bg-line sm:block" aria-hidden />

      <UserMenu />
    </header>
  );
}
